import { Mono } from "@/components/ui/mono";

interface TrainingConfigPanelProps {
  family: "tabular" | "anomaly" | "forecast";
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
  readOnly?: boolean;
}

interface FieldConfig {
  key: string;
  label: string;
  type: "text" | "number" | "select";
  options?: string[];
}

const FIELD_CONFIGS: Record<string, FieldConfig[]> = {
  tabular: [
    {
      key: "task_type",
      label: "TASK TYPE",
      type: "select",
      options: ["classification", "regression", "quantile"],
    },
    { key: "label", label: "LABEL", type: "text" },
    {
      key: "mode",
      label: "MODE",
      type: "select",
      options: ["fast", "balanced", "accurate", "extreme", "tabarena"],
    },
    { key: "eval_metric", label: "EVAL METRIC", type: "text" },
    { key: "decision_threshold", label: "DECISION THRESHOLD", type: "number" },
    { key: "time_limit", label: "TIME LIMIT", type: "number" },
    { key: "num_bag_folds", label: "NUM BAG FOLDS", type: "number" },
    { key: "num_bag_sets", label: "NUM BAG SETS", type: "number" },
    { key: "num_stack_levels", label: "NUM STACK LEVELS", type: "number" },
  ],
  anomaly: [
    {
      key: "mode",
      label: "MODE",
      type: "select",
      options: ["fast", "balanced", "accurate"],
    },
    {
      key: "ensemble_strategy",
      label: "ENSEMBLE STRATEGY",
      type: "select",
      options: ["average", "maximum", "aom", "moa"],
    },
    { key: "contamination", label: "CONTAMINATION", type: "number" },
    { key: "time_limit", label: "TIME LIMIT", type: "number" },
  ],
  forecast: [
    { key: "label", label: "LABEL", type: "text" },
    { key: "time_col", label: "TIME COL", type: "text" },
    { key: "prediction_length", label: "PREDICTION LENGTH", type: "number" },
    { key: "group_col", label: "GROUP COL", type: "text" },
    {
      key: "mode",
      label: "MODE",
      type: "select",
      options: [
        "fast",
        "balanced",
        "accurate",
        "chronos2",
        "chronos2_ensemble",
        "chronos_bolt_small",
        "chronos_bolt_base",
        "chronos_bolt_large",
      ],
    },
    { key: "freq", label: "FREQ", type: "text" },
    { key: "eval_metric", label: "EVAL METRIC", type: "text" },
    { key: "time_limit", label: "TIME LIMIT", type: "number" },
    { key: "num_val_windows", label: "NUM VAL WINDOWS", type: "number" },
  ],
};

export function TrainingConfigPanel({
  family,
  config,
  onChange,
  readOnly,
}: TrainingConfigPanelProps) {
  const fields = FIELD_CONFIGS[family] || [];

  const handleChange = (key: string, value: string, type: string) => {
    const newConfig = { ...config };

    if (value === "") {
      delete newConfig[key];
    } else if (type === "number") {
      const numValue = Number.parseFloat(value);
      if (!Number.isNaN(numValue)) {
        newConfig[key] = numValue;
      }
    } else {
      newConfig[key] = value;
    }

    onChange(newConfig);
  };

  const renderField = (field: FieldConfig) => {
    const value = config[field.key];

    if (readOnly) {
      return (
        <Mono className="text-xs text-ink">
          {value !== undefined && value !== null ? String(value) : "—"}
        </Mono>
      );
    }

    if (field.type === "select") {
      return (
        <select
          value={value !== undefined ? String(value) : ""}
          onChange={(e) => handleChange(field.key, e.target.value, field.type)}
          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
        >
          <option value="">Select...</option>
          {field.options?.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={field.type}
        value={value !== undefined ? String(value) : ""}
        onChange={(e) => handleChange(field.key, e.target.value, field.type)}
        className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
      />
    );
  };

  return (
    <div className="grid grid-cols-[120px_1fr] gap-x-6 gap-y-4">
      {fields.map((field) => (
        <div key={field.key} className="contents">
          <span className="font-mono text-xs uppercase tracking-wider text-muted self-center">
            {field.label}
          </span>
          <div>{renderField(field)}</div>
        </div>
      ))}
    </div>
  );
}
